import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Navigate, Link } from 'react-router-dom'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'

const Landing = () => {
  const { user } = useAuth()
  const [countdown, setCountdown] = useState({ days: 7, hours: 0, minutes: 0, seconds: 0 })
  const [countdownCycle, setCountdownCycle] = useState('initial') // 'initial' = 7 วันแรก, 'repeat' = 3 วันต่อๆ ไป

  // นับเวลาถอยหลังแบบวนซ้ำ
  useEffect(() => {
    // ตรวจสอบว่ามีข้อมูลการนับถอยหลังที่บันทึกไว้หรือไม่
    const savedCountdown = localStorage.getItem('countdownData');
    let countdownDate = new Date();
    
    if (savedCountdown) {
      const parsedData = JSON.parse(savedCountdown);
      countdownDate = new Date(parsedData.targetDate);
      setCountdownCycle(parsedData.cycle);
    } else {
      // ถ้าไม่มีข้อมูลที่บันทึกไว้ ให้ตั้งค่าเริ่มต้นเป็น 7 วัน
      countdownDate.setDate(countdownDate.getDate() + 7);
      
      // บันทึกข้อมูลลง localStorage
      const countdownData = {
        targetDate: countdownDate.toISOString(),
        cycle: 'initial'
      };
      localStorage.setItem('countdownData', JSON.stringify(countdownData));
    }
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = countdownDate.getTime() - now;
      
      if (distance < 0) {
        // เมื่อครบกำหนด ให้เริ่มนับใหม่
        const newCountdownDate = new Date();
        newCountdownDate.setDate(newCountdownDate.getDate() + 3); // ตั้งเป็น 3 วันสำหรับรอบต่อไป
        
        // บันทึกข้อมูลลง localStorage
        const countdownData = {
          targetDate: newCountdownDate.toISOString(),
          cycle: 'repeat'
        };
        localStorage.setItem('countdownData', JSON.stringify(countdownData));
        
        // อัปเดต state
        countdownDate = newCountdownDate;
        setCountdownCycle('repeat');
        return;
      }
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  // เพิ่มส่วนของแพ็กเกจ
  const plans = [
    {
      name: 'Free',
      price: 'ฟรี',
      color: 'green',
      features: [
        'สร้าง Prompt ได้ 1,000 ต่อวัน',
        'ใช้งานฟีเจอร์พื้นฐาน',
        'ใช้งาน Extension ฟรีได้',
      ],
    },
    {
      name: 'VIP',
      price: (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold">799.-</span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm text-gray-400">ต่ออายุเพียง</span>
            <span className="text-lg font-bold bg-gradient-to-r from-yellow-200 to-yellow-500 text-transparent bg-clip-text animate-pulse">
              99฿/เดือน
            </span>
            <span className="bg-yellow-500/20 px-2 py-0.5 rounded-full text-xs text-yellow-400">
              ประหยัด 83%
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="bg-yellow-500/20 px-2 py-0.5 rounded-full text-xs text-yellow-400">
              ราคาพิเศษ จำกัดเวลา
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">แพคเกจรายปี</span>
              <span className="text-lg font-bold bg-gradient-to-r from-green-200 to-green-500 text-transparent bg-clip-text">
                1,399฿
              </span>
              <span className="bg-green-500/20 px-2 py-0.5 rounded-full text-xs text-green-400">
                จากปกติ 1,888฿
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">ต่ออายุเพียง</span>
              <span className="text-lg font-bold bg-gradient-to-r from-green-200 to-green-500 text-transparent bg-clip-text">
                999฿/ปี
              </span>
            </div>
          </div>
        </div>
      ),
      color: 'yellow',
      features: [
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg bg-gradient-to-r from-yellow-200 to-yellow-500 text-transparent bg-clip-text">
            สร้าง Prompt ได้ไม่จำกัด
          </span>
        </div>,
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="font-bold bg-gradient-to-r from-red-200 to-red-500 text-transparent bg-clip-text">
              AI Unlock Custom GPT 🤖
            </span>
            <span className="bg-red-500/20 px-2 py-0.5 rounded text-xs text-red-400">
              New
            </span>
          </div>
          <ul className="pl-5 space-y-1 text-sm text-gray-400">
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              แนะนำคีย์เวิร์ดน่าทำ
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              แตกคียเวิรด์แบบต่างๆ
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              สร้าง Prompt แบบมืออาชีพ
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              อื่นๆที่มี และในอนาคต
            </li>
            <li className="flex items-center gap-2">
              <svg className="w-4 h-4 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-bold text-yellow-400">
                สมัครครั้งเดียวใช้ได้ตลอด ไม่ต่ออายุก็ใช้ได้
              </span>
            </li>
          </ul>
        </div>,
        <div className="flex items-center gap-2">
          <span className="font-bold bg-gradient-to-r from-yellow-200 to-yellow-500 text-transparent bg-clip-text">
            ใช้งานได้ทุกเครื่องมือ VIP ⚡️
          </span>
        </div>,
         <div className="flex items-center gap-2">
         <span className="font-bold bg-gradient-to-r from-yellow-200 to-yellow-500 text-transparent bg-clip-text">
           สอบถามส่วนตัวได้ตลอด ⚡️
         </span>
       </div>,
      ],
      recommended: true,
      badge: 'Popular'
    },
  ]

  return (
    <>
      <Helmet>
        <title>AI Unlocked - ครบวงจร สำหรับผู้ที่สนใจใช้ AI สร้างภาพขายบน Adobe Stock แบบมืออาชีพ</title>
        <meta name="description" content="สร้าง Midjourney Prompts แบบมืออาชีพ ในไม่กี่วินาี ด้วย AI Prompt Generator ที่ช่วยให้คุณสร้างภาพได้ตรงใจ" />
        <meta name="keywords" content="AI, Midjourney, Prompt Generator, AI Unlocked, สร้างภาพ AI, Midjourney Prompts, AI Art" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aiunlocked.com/" />
        <meta property="og:title" content="AI Unlocked - AI Prompt Generator" />
        <meta property="og:description" content="สร้าง Midjourney Prompts แบบมืออาชีพ ในไม่กี่วินาที" />
        <meta property="og:image" content="https://aiunlocked.com/og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://aiunlocked.com/" />
        <meta property="twitter:title" content="AI Unlocked - AI Prompt Generator" />
        <meta property="twitter:description" content="สร้าง Midjourney Prompts แบบมืออาชีพ ในไม่กี่วินาที" />
        <meta property="twitter:image" content="https://aiunlocked.com/og-image.jpg" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">
        {/* Navbar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="text-xl font-bold">
                AI Unlocked
              </Link>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    const pricingSection = document.getElementById('pricing');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-6 py-2 rounded-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold transition-all"
                >
                  ราคา
                </button>
                <Link
                  to="/register"
                  className="px-6 py-2 rounded-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 transition-all"
                >
                  สมัครสมาชิก
                </Link>
                <Link
                  to="/login"
                  className="px-6 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-all"
                >
                  เข้าสู่ระบบ
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="relative min-h-[80vh] flex items-center">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <h1 className="flex flex-col items-center gap-2 mb-4">
                <span className="text-5xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-orange-500">
                  AI Unlocked VIP
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-4">
                ครบวงจร สำหรับผู้ที่สนใจใช้ AI สร้างภาพขายบน Adobe Stock
              </p>
              <p className="text-lg md:text-xl text-gray-400 mb-12">
                เรียนรู้ทุกขั้นตอน ตั้งแต่การสร้างภาพ จนถึงการขาย พร้อมเครื่องมือที่ช่วยให้คุณทำเงินได้ง่ายขึ้น
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="group">
                  <button 
                    onClick={() => document.getElementById('ideas').scrollIntoView({ behavior: 'smooth' })}
                    className="block bg-[#1a1333] rounded-2xl p-8 hover:bg-[#231944] transition-all duration-300"
                  >
                    <div className="flex flex-col gap-3">
                      <span className="text-4xl">💡</span>
                      <h3 className="text-xl font-medium text-[#9d6cff]">ไอเดีย</h3>
                      <p className="text-sm text-gray-400">AI ช่วยคิดหัวข้อ และ Prompts</p>
                    </div>
                  </button>
                </div>

                <div className="group">
                  <button 
                    onClick={() => document.getElementById('tools').scrollIntoView({ behavior: 'smooth' })}
                    className="block bg-[#132133] rounded-2xl p-8 hover:bg-[#192a42] transition-all duration-300"
                  >
                    <div className="flex flex-col gap-3">
                      <span className="text-4xl">⚡</span>
                      <h3 className="text-xl font-medium text-[#3b82f6]">เครื่องมือ</h3>
                      <p className="text-sm text-gray-400">เครื่องมือทุ่นแรงต่างๆ</p>
                    </div>
                  </button>
                </div>

                <div className="group">
                  <button 
                    onClick={() => document.getElementById('lessons').scrollIntoView({ behavior: 'smooth' })}
                    className="block bg-[#331313] rounded-2xl p-8 hover:bg-[#421919] transition-all duration-300"
                  >
                    <div className="flex flex-col gap-3">
                      <span className="text-4xl">📚</span>
                      <h3 className="text-xl font-medium text-[#f43f5e]">บทเรียน</h3>
                      <p className="text-sm text-gray-400">วิดีโอสอนทุกขั้นตอน</p>
                    </div>
                  </button>
                </div>

                <div className="group">
                  <button 
                    onClick={() => document.getElementById('support').scrollIntoView({ behavior: 'smooth' })}
                    className="block bg-[#133321] rounded-2xl p-8 hover:bg-[#194229] transition-all duration-300"
                  >
                    <div className="flex flex-col gap-3">
                      <span className="text-4xl">🎯</span>
                      <h3 className="text-xl font-medium text-[#22c55e]">ซัพพอร์ท</h3>
                      <p className="text-sm text-gray-400">แชทกับแอดมินส่วนตัว</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section Ideas */}
        <div id="ideas" className="py-24 px-4">
          <div className="max-w-7xl mx-auto">
            {/* ส่วนฟีเจอร์เด่นเดิม */}
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Ideas</h2>
              <p className="text-gray-400 text-xl">เรามีเครื่องมือ AI ต่างๆมากมายให้สมาชิกเลือกใช้ สำหรับช่วยคิดไอเดีย และคีย์เวิร์ดต่างๆ <br />สำหรับสร้าง Prompts และยังมี "คลังคีย์เวิร์ด" ขายดีแชร์ให้อีกด้วย</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="col-start-1 col-span-3">
                <div className="grid md:grid-cols-3 gap-8">
                  {[
                    {
                      icon: "🎨",
                      title: "Custom GPT by AI Unlocked",
                      desc: "หา Keywords มาทำ Prompt ง่ายๆด้วยการใช้ Custom GPT ของ AI Unlocked แตกคีย์"
                    },
                    {
                      icon: "⚡",
                      title: "My Keywords Data",
                      desc: "แชร์ Keywords Sheet ที่ใช้จริงคอนเซ็ปต่างๆ มากกว่า 50,000 คีย์เวิร์ดให้เลือกใช้"
                    },
                    {
                      icon: "🎯",
                      title: "Prompt Generator",
                      desc: "สร้าง Prompt แบบง่ายๆในไม่กี่วินาที ด้วยเครื่องมือของเรา ทั้งบนเว็บและ โปรแกรมเสริม"
                    }
                  ].map((feature, i) => (
                    <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/10 transition-all border border-blue-500/30">
                      <div className="text-4xl mb-4 flex justify-center">{feature.icon}</div>
                      <h3 className="text-xl font-semibold mb-2 text-center">{feature.title}</h3>
                      <p className="text-gray-400 text-center">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ส่น Custom GPT เพิ่มเข้ามาใหม่ */}
            <div className="text-center mt-16 mb-6">
              <h3 className="text-5xl font-bold mb-4 bg-gradient-to-r from-red-200 to-red-500 text-transparent bg-clip-text">
               GPT by AI Unlocked
              </h3>
              <p className="text-gray-400 text-xl mt-4">
                เรามี AI สำหรับช่วยหา Keywords และคอนเซปต่าๆมากมายเพื่อสร้าง Prompt นำไปใส่ Midjourney
              </p>
              <div className="flex justify-center mt-6 mb-2">
                <img src="/images/custom-gpt.jpg" alt="Custom GPT" className="max-w-[600px] w-full h-auto rounded-lg" />
              </div>
              <p className="text-gray-400 text-xl mt-4">
                ** สมัคร Chat GPT ฟรี ใช้ได้เลย ไม่จำเป็นต้องสมัครแบบ Pro **
              </p>

            </div>

          </div>
        </div>

        {/* Tools Section */}
        <div id="tools" className="py-24 px-4 bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h2 className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl">
                VIP Tools
              </h2>
              <p className="mt-5 max-w-xl mx-auto text-xl text-gray-300">
                เครื่องมือพร้อมใช้ทุ่นแรงทุกขั้นตอนช่วยให้ Workflow ของคุณง่ายขึ้น
              </p>
            </div>
            <div className="mt-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    name: "MetaMind",
                    role: "Keywords Tools",
                    desc: "เครื่องมือช่วยคิด Title และ Keywords พร้อมฝังลงในรูปภาพอัตโนมัติ 1000 รูปแค่ 3 บาท หรือฝังฟรีด้วย Gemini",
                    image: "/images/tools/metamind.png",
                    roleColor: "text-blue-400",
                    macOS: true,
                    windows: true
                  },
                  {
                    name: "Ready Prompt",
                    role: "Prompt Tools",
                    desc: "เครื่องมือสำหรับสร้าง Prompt ด้วย AI API สำหรับ Midjourney มีทั้งแบบโปรแกรมและบนหน้าเว็บ",
                    image: "/images/tools/ready-prompt.png",
                    roleColor: "text-blue-400",
                    macOS: true,
                    windows: true
                  },
                  {
                    name: "Image Checker",
                    role: "AI Assistant",
                    desc: "เครื่องมือช่วยตรวจสอบภาพ ความคมชัด เบลอ น้อยซ์ ลายน้ำ มือ คน ช่วยคัดรูปอัตโนมัติ",
                    image: "/images/tools/checker.png",
                    roleColor: "text-blue-400",
                    macOS: true,
                    windows: true
                  },
                  {
                    name: "หมูด่าง",
                    role: "All in One Tools",
                    desc: "ตัวเอนกประสงค์ รวมเครื่องมือต่างๆของ AI Unlocked VIP ไว้ในตัวเดียว",
                    image: "/images/tools/moodang.png",
                    roleColor: "text-blue-400",
                    macOS: true,
                    windows: true
                  },
                  {
                    name: "Image Separator",
                    role: "Organization Tools",
                    desc: "แยกรูปภาพที่สร้างจาก Midjourney ออกเป็น 2 และ 4 ชุด ตามต้องการ",
                    image: "/images/tools/separator.png",
                    roleColor: "text-blue-400",
                    windows: true
                  },
                  {
                    name: "Unlocked Spy (รออัพเดท)",
                    role: "Research Tools",
                    desc: "เครื่องมือช่วยสร้าง prompt จากรูปภาพคู่แข่งบน Adobe Stock",
                    image: "/images/tools/spy.png",
                    roleColor: "text-blue-400",
                    windows: true
                  }
                ].map((tool, i) => (
                  <div key={i} className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/10 transition-all border border-blue-500/30">
                    <div className="absolute top-3 right-3 flex gap-2">
                      {tool.macOS && (
                        <span className="text-xs bg-gray-800/80 text-gray-300 px-2 py-1 rounded-full">
                          MacOS
                        </span>
                      )}
                      {tool.windows && (
                        <span className="text-xs bg-gray-800/80 text-gray-300 px-2 py-1 rounded-full">
                          Windows
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <img src={tool.image} alt={tool.name} className="w-12 h-12 object-cover rounded-xl" />
                      <div>
                        <h3 className="font-semibold">{tool.name}</h3>
                        <p className={`text-sm ${tool.roleColor}`}>{tool.role}</p>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mt-4">{tool.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
                    {/* Learn Section */}
                    <div id="lessons" className="py-32 px-4 bg-gradient-to-b from-black/30 to-red-900/20 w-full">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-16">
                  <h2 className="text-4xl md:text-5xl font-bold mb-6">เรียนรู้การใช้งาน AI อย่างมืออาชีพ</h2>
                  <p className="text-xl md:text-2xl text-gray-400">เรียนรู้ทุกขั้นตอนการใช้งาน AI ตั้งแต่เริ่มต้น จนสามารถสร้างรายได้</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 hover:bg-white/10 transition-all border border-red-500/30">
                    <div className="text-3xl mb-3 text-center">👋</div>
                    <h3 className="text-lg font-semibold mb-2 text-center bg-gradient-to-r from-red-400 to-red-600 text-transparent bg-clip-text">
                    มือใหม่ 0-100
                    </h3>
                    <p className="text-gray-400 text-center text-sm">
                      ทุกขั้นตอน ตั้งแต่สมัครสมาชิกจนถึง <br />อัพโหลดรูปขายบนเว็บ Adobe Stock 
                    </p>
                  </div>

                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 hover:bg-white/10 transition-all border border-red-500/30">
                    <div className="text-3xl mb-3 text-center">💡</div>
                    <h3 className="text-lg font-semibold mb-2 text-center bg-gradient-to-r from-red-400 to-red-600 text-transparent bg-clip-text">
                      เทคนิคต่างๆ
                    </h3>
                    <p className="text-gray-400 text-center text-sm">
                      เทคนิคต่างๆ สำหรับการใช้ AI <br />และการขายรูปบน Adobe Stock
                    </p>
                  </div>

                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 hover:bg-white/10 transition-all border border-red-500/30">
                    <div className="text-3xl mb-3 text-center">🔄</div>
                    <h3 className="text-lg font-semibold mb-2 text-center bg-gradient-to-r from-red-400 to-red-600 text-transparent bg-clip-text">
                      คู่มือและอัพเดท
                    </h3>
                    <p className="text-gray-400 text-center text-sm">
                      คู่มือการใช้งาน และ อัพเดทฟีเจอร์ใหม่ๆ <br /> ของเครื่องมือต่างๆใน VIP Tools
                    </p>
                  </div>
                </div>
              </div>
            </div>

        {/* AI Unlocked Metamind Section */}
        <div className="py-24 px-4">
          <div className="max-w-7xl mx-auto">







            {/* Pricing Section */}
            <div id="pricing" className="py-24 px-4 bg-black/30">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold mb-4">แพ็คเกจที่ใช่สำหรับคุณ</h2>
                  <p className="text-gray-400 text-xl">เลือกแพ็คเกจที่เหมาะกับความต้องการของคุณ</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  {/* Free Plan */}
                  <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-800 hover:border-green-500/50 transition-colors">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold mb-2 text-green-400">Free</h2>
                      <div className="text-3xl font-bold">ฟรี</div>
                    </div>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">สร้าง Prompt ได้ 1,000 ต่อวัน</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">ใช้งานฟีเจอร์พื้นฐาน</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">ใช้งาน Extension ฟรีได้</span>
                      </li>
                    </ul>
                  </div>

                  {/* VIP Plan */}
                  <div className="bg-gray-900/50 rounded-lg p-6 border border-yellow-500/50 hover:border-yellow-400/50 transition-colors transform scale-105 relative">
                    <div className="absolute -top-3 -right-3">
                      <span className="inline-flex items-center px-4 py-1 rounded-full text-xs font-medium bg-yellow-500 text-black">
                        Popular
                      </span>
                    </div>
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold mb-2 text-yellow-400">VIP</h2>
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-3xl font-bold">799.-</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-sm text-gray-400">ต่ออายุเพียง</span>
                          <span className="text-lg font-bold bg-gradient-to-r from-yellow-200 to-yellow-500 text-transparent bg-clip-text animate-pulse">
                            99฿/เดือน
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-sm text-gray-400">แพคเกจรายปี</span>
                          <span className="text-lg font-bold bg-gradient-to-r from-green-200 to-green-500 text-transparent bg-clip-text">
                            1,399฿
                          </span>
                          <span className="bg-green-500/20 px-2 py-0.5 rounded-full text-xs text-green-400">
                            จากปกติ 1,888฿
                          </span>
                        </div>
                      </div>
                    </div>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">สร้าง Prompt ได้ไม่จำกัด</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">AI Unlock Custom GPT 🤖</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">ใช้ได้ทุกเครื่องมือ VIP⚡️</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div className="flex items-center gap-2">
                          <span className="font-bold bg-gradient-to-r from-yellow-200 to-yellow-500 text-transparent bg-clip-text">SellJung ระดับ Gold</span>
                          <span className="bg-red-500/20 px-2 py-0.5 rounded-full text-xs text-red-400 animate-pulse">หมด</span>
                        </div>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">สอบถามส่วนตัวได้ตลอด⚡️</span>
                      </li>
                    </ul>
                  </div>

                  {/* Lifetime Plan */}
                  <div className="bg-gray-900/50 rounded-lg p-6 border border-blue-500/50 hover:border-blue-400/50 transition-colors">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold mb-2 text-blue-400">VIP ตลอดชีพ</h2>
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-3xl font-bold">2,399.-</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-sm text-gray-400">จ่ายครั้งเดียวจบ</span>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="bg-red-500/20 px-3 py-1 rounded-full text-xs text-red-400 animate-pulse font-medium">
                            รับสมัครจำนวนจำกัด
                          </span>
                          <span className="bg-red-500/20 px-3 py-1 rounded-full text-xs text-red-400">
                            เหลือ 20 ที่
                          </span>
                        </div>
                      </div>
                    </div>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">ทุกฟีเจอร์จากแพ็คเกจ VIP</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">ใช้งานได้ตลอดชีพ</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div className="flex items-center gap-2">
                          <span className="font-bold bg-gradient-to-r from-blue-200 to-blue-500 text-transparent bg-clip-text">SellJung ระดับ Diamond</span>
                          <span className="bg-red-500/20 px-2 py-0.5 rounded-full text-xs text-red-400 animate-pulse">หมด</span>
                        </div>
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div className="flex items-center gap-2">
                          <span className="font-bold bg-gradient-to-r from-blue-200 to-blue-500 text-transparent bg-clip-text">รับสมัครจำนวนจำกัด</span>
                          <span className="bg-blue-500/20 px-2 py-0.5 rounded text-xs text-blue-400">limited</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                         
              </div>
            </div>

            {/* FAQ Section */}
            <div className="py-24 px-4 bg-black/30">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold mb-4">คำถามที่พบบ่อย</h2>
                  <p className="text-gray-400 text-xl">รวมคำถามที่ลูกค้าถามเข้ามาบ่อยๆ</p>
                </div>

                <div className="space-y-6">
                  {[
                    {
                      q: "ต้องมีพื้นฐานการใช้งาน Midjourney ไหม?",
                      a: "ไม่จำเป็นต้องมีพื้นฐานครับ เพราะเรามีคู่มือและวิดีโอสอนการใช้งานให้ดูฟรี"
                    },
                    {
                      q: "สมัคร VIP 799.- แล้วได้อะไรบ้าง?",
                      a: (
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 mt-1">
                              <svg className="text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                            <span className="text-yellow-400 font-medium">AI Prompt Generator</span> ตัวช่วยสร้าง Prompt จากคีย์เวิร์ดสำหรับ Midjourney
                          </div>
                          
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 mt-1">
                              <svg className="text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2v4l.586-.586z"/>
                              </svg>
                            </span>
                            <span className="text-red-400 font-medium">Custom GPT</span> ตัวช่วยหาคีย์เวิร์ดสำหรับสร้าง Prompt
                          </div>

                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 mt-1">
                              <svg className="text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                            <span className="text-yellow-400 font-medium">MetaMind</span> ตัวช่วยตั้ง Title Keywords แถมฟรีจำนวนจำกัด (หมดโปรแถมจะ 899.-)
                          </div>

                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 mt-1">
                              <svg className="text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                            <span className="text-yellow-400 font-medium">Image Checker</span> ใช้ AI ช่วยเช็ครูปภาพและคัดแยกภาพเสีย
                          </div>

                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 mt-1">
                              <svg className="text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                            <span className="text-yellow-400 font-medium">Ready Prompt</span> สร้าง Prompt ไฟล์ .txt จากคีย์ที่ต้องการด้วย OpenAI API
                          </div>

                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 mt-1">
                              <svg className="text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2v4l.586-.586z"/>
                              </svg>
                            </span>
                            <span className="text-blue-400 font-medium">ห้อง VIP Discord สำหรับสมาชิก แจก VIP Tools อื่นๆ</span>
                          </div>
                        </div>
                      )
                    },
                    {
                      q: "ต้องสมัคร ChatGPT 20$ ต่อเดือนเพื่อใช้งานไหม?",
                      a: "ไม่ต้องสมัครครับ ตัวฟรีก็สามารถใช้งานได้ แค่จะจำกัดหน่อย แต่เราใช้ไม่เยอะอยู่แล้วครับ"
                    },
                    {
                      q: "มีการต่ออายุรายเดือนไหม?",
                      a: "แพ็กเกจ VIP มีค่าต่ออายุ 99 บาท/เดือน ส่วน Custom GPT สมัครครั้งเดียวใช้ได้ตลอด ไม่ต่ออายุก็ใช้ได้ครับ"
                    },
                    {
                      q: "การสร้างภาพ AI ขายบน Adobe Stock ต้องมีความรู้หรือใช้อะไรบ้าง",
                      a: (
                        <div className="space-y-3">
                          <div className="font-medium text-gray-300">ใช้คอมเป็น มีเวลาเปิดคอม ก็ทำได้เลยครับ ส่วนค่าใช้จ่ายที่จำเป็นต้องมี:</div>
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 mt-1">
                              <svg className="text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                            </span>
                            <div className="text-yellow-400 font-medium">Midjourney:</div> <div className="text-yellow-400">$30/เดือน</div>
                          </div>
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 mt-1">
                              <svg className="text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                            </span>
                            <div className="text-green-400 font-medium">ค่า API ตั้งชื่อภาพ</div><div className="text-green-400">1,000 รูปประมาณ 3 บาท</div>
                          </div>
                          <div className="mt-4 p-3 bg-gray-800/50 rounded-lg">
                            <div className="text-lg font-medium text-white">รวมค่าใช้จ่ายหลัก เช่า Midjourney <span className="text-yellow-400">$30/เดือน</span></div>
                            <div className="text-lg font-medium text-white">ค่าใช้จ่าย API เติม 5$ ใช้ได้ยาวๆ<span className="text-yellow-400"> ตย. 1 เดือนทำได้ 5,000 รูปก็แค่ 15 บาทเท่านั้น</span></div>
                            <div className="text-lg font-medium text-white">**สรุป ถ้าไม่รวมค่าสมัครสมาชิก VIP จะมีค่าใช้จ่ายรายเดือนประมาณเดือนละ 1,000 บาทนะครับ</div>                          
                          </div>
                        </div>
                      )
                    }
                  ].map((faq, i) => (
                    <div 
                      key={i}
                      className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800 hover:border-gray-700 transition-colors"
                    >
                      <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                        <span className="text-red-400">Q:</span>
                        {faq.q}
                      </h3>
                      <div className="text-gray-400 pl-6">
                        {faq.a}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-center mt-12">
                              {/* About Creator Section */}
            <div id="support" className="py-24 px-4 bg-black/30">
              <div className="max-w-4xl mx-auto">

                  <h2 className="text-4xl font-bold text-yellow-400 mb-4">Support by Creator</h2>
                  <p className="text-gray-400 text-xl">มือใหม่สมัครแล้วติดตรงไหน สามารถทักสอบถามแบบ 1-1 กับแอดมินได้ตลอด ทั้งใน Discord และเฟซบุ๊ค</p>
                </div>

            </div>
                  <div className="flex justify-center gap-4">
                    <a 
                      href="https://www.facebook.com/aiunlockedvip"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                      </svg>
                      สอบถามเพิ่มเติมที่ Facebook
                    </a>

                    <a 
                      href="https://discord.com/invite/MxgKZkydXJ"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                      </svg>
                      สอบถามเพิ่มเติมที่ Discord
                    </a>
                  </div>
                </div>
              </div>
            </div>


            {/* Footer Menu */}
            <div className="py-8 border-t border-gray-800">
              <div className="max-w-7xl mx-auto">

                <div className="text-center text-gray-500 text-xs mt-4">
                  2025 AI Unlocked. All rights reserved. AI Unlocked Prompt Generator v2.0
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Landing